import { Routes } from '@angular/router';
import { PropertyServiceGuard } from './guards/propertyService/property-service.guard';
import { AuthGuard } from './handlers/auth.guard';
import { MenuToNativeId } from './services/bridge/bridge.service';
import { LoginComponent } from './views/public/login/login.component';

export interface DataPage {
  level: number;
  page: MenuToNativeId;
  uid: string;
}

export const appRoutes: Routes = [
  // authenticated routes
  {
    path: 'home',
    loadComponent: () => import('./views/home/home.component').then((m) => m.HomeComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.HOME_PROPERTY } as DataPage,
  },
  {
    path: 'property/vcard/:eemoId',
    loadComponent: () =>
      import('./views/property/property-vcard/property-vcard.component').then((m) => m.PropertyVCardComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'property/:uid/feedback/:sharingUid',
    loadComponent: () => import('./views/property/property/property.component').then((m) => m.PropertyComponent),
    canActivate: [AuthGuard],
    data: { level: 2, page: MenuToNativeId.PROP_MAIN } as DataPage,
  },
  {
    path: 'property/:uid',
    loadComponent: () => import('./views/property/property/property.component').then((m) => m.PropertyComponent),
    data: { level: 2, page: MenuToNativeId.PROP_MAIN } as DataPage,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'main',
        loadComponent: () =>
          import('./views/property/property-main/property-main.component').then((m) => m.PropertyMainComponent),
        data: { level: 2, page: MenuToNativeId.PROP_MAIN } as DataPage,
      },
      {
        path: 'booklet',
        loadComponent: () =>
          import('./pages/property/prop-booklet-page/prop-booklet-page.component').then(
            (m) => m.PropBookletPageComponent,
          ),
        data: { level: 2, page: MenuToNativeId.BOOKLET } as DataPage,
      },
      {
        path: 'sale',
        loadComponent: () =>
          import('./pages/property/prop-sale-page/prop-sale-page.component').then((m) => m.PropSalePageComponent),
        data: { level: 2, page: MenuToNativeId.PROP_SALE } as DataPage,
      },
      {
        path: 'infos',
        loadComponent: () =>
          import('./views/property/property-infos/property-infos.component').then((m) => m.PropertyInfosComponent),
        data: { level: 2, page: MenuToNativeId.PROP_INFO } as DataPage,
      },
      {
        loadComponent: () =>
          import('./pages/property/prop-fiche-secours-page/prop-fiche-secours-page.component').then(
            (m) => m.PropFicheSecoursPageComponent,
          ),
        path: 'rescue',
        data: { level: 3, page: MenuToNativeId.FICHE_SECOURS } as DataPage,
      },
      {
        loadComponent: () =>
          import('./pages/property/prop-location-page/prop-location-page.component').then(
            (m) => m.PropLocationPageComponent,
          ),
        path: 'location',
        data: { level: 3, page: MenuToNativeId.LOCATION } as DataPage,
      },
      {
        loadComponent: () =>
          import('./pages/property/prop-building-page/prop-building-page.component').then(
            (m) => m.PropBuildingPageComponent,
          ),
        path: 'building',
        data: { level: 3, page: MenuToNativeId.BUILDING } as DataPage,
      },
      {
        loadComponent: () =>
          import('./pages/property/prop-energy-page/prop-energy-page.component').then((m) => m.PropEnergyPageComponent),
        path: 'energy',
        data: { level: 3, page: MenuToNativeId.ENERGY } as DataPage,
      },
      {
        loadComponent: () =>
          import('./pages/property/prop-financial-page/prop-financial-page.component').then(
            (m) => m.PropFinancialPageComponent,
          ),
        path: 'financial',
        data: { level: 3, page: MenuToNativeId.FINANCIAL } as DataPage,
      },
      {
        loadComponent: () =>
          import('./pages/property/prop-people-page/prop-people-page.component').then((m) => m.PropPeoplePageComponent),
        path: 'people',
        data: { level: 3, page: MenuToNativeId.PEOPLE } as DataPage,
      },
      {
        loadComponent: () =>
          import('./pages/property/prop-condo-page/prop-condo-page.component').then((m) => m.PropCondoPageComponent),
        path: 'condo',
        data: { level: 3, page: MenuToNativeId.CONDOMINIUM } as DataPage,
      },
      {
        path: 'photos',
        loadComponent: () =>
          import('./pages/property/prop-photos-page/prop-photos-page.component').then((m) => m.PropPhotosPageComponent),
        data: { level: 2, page: MenuToNativeId.PHOTO } as DataPage,
      },
      {
        loadComponent: () =>
          import('./pages/property/prop-equipment-page/prop-equipment-page.component').then(
            (m) => m.PropEquipmentPageComponent,
          ),
        path: 'equipments',
        data: { level: 2, page: MenuToNativeId.EQUIPMENT } as DataPage,
      },
      {
        path: 'tracking',
        loadComponent: () => import('./views/tracking/tracking/tracking.component').then((m) => m.TrackingComponent),
        data: { level: 2, page: MenuToNativeId.TRACKING } as DataPage,
      },
      {
        loadComponent: () =>
          import('./pages/property/prop-timeline-page/prop-timeline-page.component').then(
            (m) => m.PropTimelinePageComponent,
          ),
        path: 'timeline',
        data: { level: 3, page: MenuToNativeId.TIMELINE } as DataPage,
      },
      {
        loadComponent: () =>
          import('./pages/property/prop-consumption-page/prop-consumption-page.component').then(
            (m) => m.PropConsumptionPageComponent,
          ),
        path: 'consumption',
        data: { level: 3, page: MenuToNativeId.CONSUMPTION } as DataPage,
      },
      {
        loadComponent: () =>
          import('./pages/property/prop-maintenance-page/prop-maintenance-page.component').then(
            (m) => m.PropMaintenancePageComponent,
          ),
        path: 'maintenance',
        data: { level: 3, page: MenuToNativeId.MAINTENANCE } as DataPage,
      },
      {
        loadComponent: () =>
          import('./pages/property/prop-work-page/prop-work-page.component').then((m) => m.PropWorkPageComponent),
        path: 'work',
        data: { level: 3, page: MenuToNativeId.WORK } as DataPage,
      },
      {
        loadComponent: () =>
          import('./pages/property/prop-tracking-financial-page/prop-tracking-financial-page.component').then(
            (m) => m.PropTrackingFinancialPageComponent,
          ),
        path: 'tracking-financial',
        data: { level: 3, page: MenuToNativeId.TRACKING_FINANCIAL } as DataPage,
      },
      {
        path: 'vault',
        loadComponent: () =>
          import('./pages/property/prop-vault-page/prop-vault-page.component').then((m) => m.PropVaultPageComponent),
        data: { level: 2, page: MenuToNativeId.VAULT } as DataPage,
      },
      {
        path: 'shares',
        loadComponent: () =>
          import('./pages/property/prop-shares-page/prop-shares-page.component').then((m) => m.PropSharesPageComponent),
        data: { level: 2, page: MenuToNativeId.PROP_SHARES } as DataPage,
      },
      {
        path: 'services',
        loadComponent: () =>
          import('./pages/property/prop-services-page/prop-services-page.component').then(
            (m) => m.PropServicesPageComponent,
          ),
        data: { level: 2, page: MenuToNativeId.PROP_SERVICES } as DataPage,
        canActivate: [PropertyServiceGuard],
      },
      { path: '', redirectTo: 'main', pathMatch: 'full' },
    ],
  },
  {
    path: 'condo-mngt/:uid',
    loadComponent: () =>
      import('./views/property/condo-management/condo-management.component').then((m) => m.CondoManagementComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.MAIN_CONDO } as DataPage,
  },
  {
    path: 'shares',
    loadComponent: () => import('./views/shares/shares.component').then((m) => m.SharesComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.SHARES } as DataPage,
  },
  {
    path: 'partners',
    loadComponent: () => import('./views/partners/partners.component').then((m) => m.PartnersComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.PARTNERS } as DataPage,
  },
  {
    path: 'admin',
    loadComponent: () => import('./views/admin/admin-nav/admin.component').then((m) => m.AdminComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.ADMIN } as DataPage,
  },
  {
    path: 'profile',
    loadComponent: () => import('./views/profile/profile.component').then((m) => m.ProfileComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.PROFILE } as DataPage,
  },
  {
    path: 'dropbox',
    loadComponent: () => import('./views/dropbox/dropbox.component').then((m) => m.DropboxComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.DROPBOX } as DataPage,
  },
  {
    path: 'kpi',
    loadComponent: () => import('./views/kpis/kpis.component').then((m) => m.KpisComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.KPIS } as DataPage,
  },
  {
    path: 'connectors',
    loadComponent: () => import('./views/connectors/connectors.component').then((m) => m.ConnectorsComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.CONNECTORS } as DataPage,
  },
  {
    path: 'contacts',
    loadComponent: () => import('./views/contacts/contacts.component').then((m) => m.ContactsComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.CONTACTS } as DataPage,
  },
  {
    path: 'visit-feedbacks',
    loadComponent: () =>
      import('./views/visit-feedbacks/visit-feedbacks.component').then((m) => m.VisitFeedbacksComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.VISIT_FEEDBACKS } as DataPage,
  },
  {
    path: 'notifications',
    loadComponent: () => import('./views/notifications/notifications.component').then((m) => m.NotificationsComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.NOTIFICATIONS } as DataPage,
  },
  {
    path: 'notifications/:uid',
    loadComponent: () => import('./views/notifications/notifications.component').then((m) => m.NotificationsComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.NOTIFICATIONS } as DataPage,
  },
  {
    path: 'help',
    loadComponent: () => import('./views/help/help.component').then((m) => m.HelpComponent),
    canActivate: [AuthGuard],
    data: { level: 1, page: MenuToNativeId.HELP } as DataPage,
  },
  // public routes
  { path: 'login', component: LoginComponent },
  {
    path: 'login/callback',
    loadComponent: () =>
      import('./views/public/login-callback/login-callback.component').then((m) => m.LoginCallbackComponent),
  },
  {
    path: 'login/guest',
    loadComponent: () => import('./views/public/login-guest/login-guest.component').then((m) => m.LoginGuestComponent),
  },
  {
    path: 'logout',
    loadComponent: () => import('./views/public/logout/logout.component').then((m) => m.LogoutComponent),
  },
  {
    path: 'register',
    loadComponent: () => import('./views/public/register/register.component').then((m) => m.RegisterComponent),
  },
  {
    path: 'password-lost',
    loadComponent: () =>
      import('./views/public/password-lost/password-lost.component').then((m) => m.PasswordLostComponent),
  },
  {
    path: 'users/:uid/email-validate/:key',
    loadComponent: () =>
      import('./views/public/email-validation/email-validation.component').then((m) => m.EmailValidationComponent),
  },

  { path: '', redirectTo: '/home', pathMatch: 'full' },
  // otherwise redirect to not found
  {
    path: '**',
    loadComponent: () => import('./views/not-found/not-found.component').then((m) => m.NotFoundComponent),
    canActivate: [AuthGuard],
  },
];
